import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Shield,
  ShieldAlert,
  UserCheck,
  UserPlus,
  Search,
  RefreshCw,
  Edit3,
  Key,
  LogOut,
  Trash2,
  Boxes,
  Activity,
  Layers,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  X,
  Lock,
  Sparkles,
  Loader2
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import CreateUserModal from './CreateUserModal.jsx';
import EditUserModal from './EditUserModal.jsx';
import ChangePasswordModal from './ChangePasswordModal.jsx';
import styles from './AdminDashboard.css';
import stemverseLogo from '../menu-bar/stemverse-logo.png';
import { ALL_EXTENSION_MODULES, EXTENSION_CATEGORIES } from '../../lib/libraries/extensions/extensionModules';

export default function AdminDashboard({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    totalRegularUsers: 0,
    totalActiveLogins: 0,
    totalProjects: 0
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'modules' | 'overview'
  const [matrixCategory, setMatrixCategory] = useState('All');
  const [matrixSearch, setMatrixSearch] = useState('');

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [resetLoginUser, setResetLoginUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes] = await Promise.all([
        apiClient.get('/api/users'),
        apiClient.get('/api/users/stats/overview').catch(() => ({ data: { stats: {} } }))
      ]);

      if (usersRes.data && Array.isArray(usersRes.data.users)) {
        setUsers(usersRes.data.users);
      } else if (Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
      }

      if (statsRes.data && statsRes.data.stats) {
        setStats(statsRes.data.stats);
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
      showToast(err.response?.data?.error || 'Failed to load users data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  if (!isOpen) return null;

  // Handle User Creation
  const handleCreateUser = async (formData) => {
    try {
      setIsSubmitting(true);
      const response = await apiClient.post('/api/users', formData);
      if (response.data.success) {
        showToast('User created successfully!');
        setIsCreateModalOpen(false);
        fetchData();
      }
    } catch (err) {
      console.error('Create user error:', err);
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Edit User Access
  const handleUpdateUser = async (updatedData) => {
    try {
      setIsSubmitting(true);
      const response = await apiClient.put(`/api/users/${editingUser._id}/access`, updatedData);
      if (response.data.success) {
        showToast('User permissions updated successfully!');
        setEditingUser(null);
        fetchData();
      }
    } catch (err) {
      console.error('Update user error:', err);
      showToast(err.response?.data?.error || 'Failed to update user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Change Password
  const handleChangePassword = async (newPassword) => {
    try {
      setIsSubmitting(true);
      const response = await apiClient.put(`/api/users/${passwordUser._id}/password`, { newPassword });
      if (response.data.success) {
        showToast(`Password for ${passwordUser.email} updated successfully!`);
        setPasswordUser(null);
      }
    } catch (err) {
      console.error('Password change error:', err);
      showToast(err.response?.data?.error || 'Failed to update password', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Reset Active Logins
  const handleResetLogins = async () => {
    if (!resetLoginUser) return;
    try {
      setIsSubmitting(true);
      const response = await apiClient.put(`/api/users/${resetLoginUser._id}/reset-logins`);
      if (response.data.success) {
        showToast(`Active sessions for ${resetLoginUser.name} were reset to 0`);
        setResetLoginUser(null);
        fetchData();
      }
    } catch (err) {
      console.error('Reset logins error:', err);
      showToast(err.response?.data?.error || 'Failed to reset logins', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Delete User
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      setIsSubmitting(true);
      const response = await apiClient.delete(`/api/users/${deletingUser._id}`);
      if (response.data.success) {
        showToast(`User ${deletingUser.name} deleted successfully`);
        setDeletingUser(null);
        fetchData();
      }
    } catch (err) {
      console.error('Delete user error:', err);
      showToast(err.response?.data?.error || 'Failed to delete user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter users based on search & role
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.name && user.name.toLowerCase().includes(search.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(search.toLowerCase())) ||
      (user.username && user.username.toLowerCase().includes(search.toLowerCase()));

    const matchesRole =
      roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  return (
    <div className={styles.adminRoot}>
      {/* Toast Notification */}
      {toast && (
        <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <header className={styles.adminHeader}>
        <div className={styles.adminBrand}>
          <img src={stemverseLogo} alt="StemVerse Logo" className={styles.adminLogo} />
          <div className={styles.adminTitleWrap}>
            <h1 className={styles.adminTitle}>StemVerse Admin Console</h1>
            <span className={styles.adminSubtitle}>Role & Access Management</span>
          </div>
        </div>

        <div className={styles.adminHeaderActions}>
          <div className={styles.adminBadge}>
            <Shield size={14} />
            <span>Administrator Portal</span>
          </div>

          <button
            className={styles.btnSecondary}
            onClick={fetchData}
            title="Refresh Data"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>

          <button
            className={styles.btnPrimary}
            onClick={onClose}
          >
            <ArrowLeft size={16} />
            <span>Back to Workspace</span>
          </button>
        </div>
      </header>

      {/* Body */}
      <div className={styles.adminBody}>
        {/* Sidebar */}
        <aside className={styles.adminSidebar}>
          <div
            className={`${styles.navItem} ${activeTab === 'users' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={18} />
            <span>User Management</span>
          </div>

          <div
            className={`${styles.navItem} ${activeTab === 'modules' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('modules')}
          >
            <Boxes size={18} />
            <span>Module Permissions</span>
          </div>

          <div
            className={`${styles.navItem} ${activeTab === 'overview' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Activity size={18} />
            <span>System Analytics</span>
          </div>

          <div style={{ marginTop: 'auto', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#38bdf8', fontSize: '0.8rem', fontWeight: 600 }}>
              <Sparkles size={14} />
              <span>StemBlocks RBAC</span>
            </div>
            <p style={{ margin: '6px 0 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
              Assign individual modules or promote users to full Admin status.
            </p>
          </div>
        </aside>

        {/* Main Content */}
        <main className={styles.adminMain}>
          {/* Stats Overview */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={`${styles.statIconWrap} ${styles.statIconUsers}`}>
                <Users size={24} />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Total Users</span>
                <span className={styles.statValue}>{stats.totalUsers || users.length}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={`${styles.statIconWrap} ${styles.statIconActive}`}>
                <UserCheck size={24} />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Active Logins</span>
                <span className={styles.statValue}>{stats.totalActiveLogins || 0}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={`${styles.statIconWrap} ${styles.statIconAdmins}`}>
                <Shield size={24} />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Admin Accounts</span>
                <span className={styles.statValue}>{stats.totalAdmins || users.filter(u => u.role === 'admin').length}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={`${styles.statIconWrap} ${styles.statIconProjects}`}>
                <Layers size={24} />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Total Projects</span>
                <span className={styles.statValue}>{stats.totalProjects || 0}</span>
              </div>
            </div>
          </div>

          {/* Tab 1: User Management */}
          {activeTab === 'users' && (
            <div className={styles.contentCard}>
              <div className={styles.toolbar}>
                <div className={styles.searchFilterGroup}>
                  <div className={styles.searchBox}>
                    <Search size={16} className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search users by name, email, or username..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>

                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className={styles.selectInput}
                  >
                    <option value="all">All Roles</option>
                    <option value="user">Users Only</option>
                    <option value="admin">Admins Only</option>
                  </select>
                </div>

                <button
                  className={styles.btnPrimary}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <UserPlus size={16} />
                  <span>Create User</span>
                </button>
              </div>

              {/* Users Table */}
              <div className={styles.tableWrap}>
                <table className={styles.userTable}>
                  <thead>
                    <tr>
                      <th className={styles.stickyColLeft}>User Details</th>
                      <th>Role</th>
                      <th>Login Status</th>
                      <th>Max Logins</th>
                      <th>Accessible Modules</th>
                      <th>Joined Date</th>
                      <th className={styles.stickyColRight} style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                          <Loader2 size={28} className="animate-spin" style={{ margin: '0 auto 12px auto', color: '#38bdf8' }} />
                          <div style={{ color: '#94a3b8' }}>Loading users directory...</div>
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          No users found matching your search and filter criteria.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map(user => {
                        const isOnline = user.isUserLoggedin || (user.numberOfLogins > 0);
                        const isAdmin = user.role === 'admin';

                        return (
                          <tr key={user._id}>
                            {/* User Name & Email */}
                            <td className={styles.stickyColLeft}>
                              <div className={styles.userPrimaryInfo}>
                                <span className={styles.userName}>{user.name || 'Unnamed User'}</span>
                                <span className={styles.userEmail}>{user.email}</span>
                                {user.username && (
                                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>@{user.username}</span>
                                )}
                              </div>
                            </td>

                            {/* Role Badge */}
                            <td>
                              <span className={`${styles.roleBadge} ${isAdmin ? styles.roleAdmin : styles.roleUser}`}>
                                {user.role || 'user'}
                              </span>
                            </td>

                            {/* Online / Active Logins */}
                            <td>
                              <div className={styles.statusPill}>
                                <span className={`${styles.statusDot} ${isOnline ? styles.statusOnline : styles.statusOffline}`} />
                                <span>{user.numberOfLogins || 0} active</span>
                              </div>
                            </td>

                            {/* Max Logins */}
                            <td>
                              <span style={{ fontWeight: 600, color: '#f1f5f9' }}>
                                {user.numberOfMaxLogins || 1} max
                              </span>
                            </td>

                            {/* Accessible Modules */}
                            <td>
                              {isAdmin ? (
                                <button
                                  type="button"
                                  onClick={() => setEditingUser(user)}
                                  className={styles.moduleSummaryPill}
                                  style={{
                                    background: 'rgba(168, 85, 247, 0.15)',
                                    borderColor: 'rgba(168, 85, 247, 0.35)',
                                    color: '#d8b4fe'
                                  }}
                                  title="Click to manage access"
                                >
                                  <Shield size={12} />
                                  <span>All {ALL_EXTENSION_MODULES.length} Modules (Admin)</span>
                                </button>
                              ) : Array.isArray(user.accessibleModules) && user.accessibleModules.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setEditingUser(user)}
                                  className={styles.moduleSummaryPill}
                                  style={{
                                    background: 'rgba(56, 189, 248, 0.12)',
                                    borderColor: 'rgba(56, 189, 248, 0.35)',
                                    color: '#38bdf8'
                                  }}
                                  title="Click to edit assigned modules"
                                >
                                  <Boxes size={12} />
                                  <span>{user.accessibleModules.length} of {ALL_EXTENSION_MODULES.length} Assigned</span>
                                  <Edit3 size={10} style={{ opacity: 0.7 }} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setEditingUser(user)}
                                  className={styles.moduleSummaryPill}
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.04)',
                                    borderColor: 'rgba(255, 255, 255, 0.15)',
                                    color: '#94a3b8',
                                    borderStyle: 'dashed'
                                  }}
                                  title="Click to assign modules"
                                >
                                  <span>+ Assign Modules</span>
                                </button>
                              )}
                            </td>

                            {/* Joined Date */}
                            <td style={{ fontSize: '0.8rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                              {user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : 'N/A'}
                            </td>

                            {/* Action Buttons */}
                            <td className={styles.stickyColRight} style={{ textAlign: 'right' }}>
                              <div className={styles.actionGroup} style={{ justifyContent: 'flex-end' }}>
                                <button
                                  className={styles.actionBtn}
                                  onClick={() => setEditingUser(user)}
                                  title="Edit Access & Modules"
                                >
                                  <Edit3 size={15} />
                                </button>

                                <button
                                  className={styles.actionBtn}
                                  onClick={() => setPasswordUser(user)}
                                  title="Change Password"
                                >
                                  <Key size={15} />
                                </button>

                                <button
                                  className={styles.actionBtn}
                                  onClick={() => setResetLoginUser(user)}
                                  title="Reset Active Logins"
                                >
                                  <LogOut size={15} />
                                </button>

                                <button
                                  className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                  onClick={() => setDeletingUser(user)}
                                  title="Delete User"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab 2: Module Permissions Matrix */}
          {activeTab === 'modules' && (
            <div className={styles.contentCard}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#ffffff' }}>Module Access Matrix</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                      Quick overview of individual module access rights. Click Edit to instantly modify any user's modules.
                    </p>
                  </div>

                  <div style={{ position: 'relative', width: '200px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                    <input
                      type="text"
                      placeholder="Search matrix..."
                      value={matrixSearch}
                      onChange={(e) => setMatrixSearch(e.target.value)}
                      className={styles.searchInput}
                      style={{ paddingLeft: '32px', height: '34px', fontSize: '0.8rem' }}
                    />
                  </div>
                </div>

                {/* Category Pills */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
                  {['All', ...EXTENSION_CATEGORIES].map(cat => {
                    const isActive = matrixCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => setMatrixCategory(cat)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: isActive ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                          background: isActive ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.03)',
                          color: isActive ? '#38bdf8' : '#94a3b8'
                        }}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {(() => {
                const visibleModules = ALL_EXTENSION_MODULES.filter(m => {
                  const matchCat = matrixCategory === 'All' || m.category === matrixCategory;
                  const matchSearch = !matrixSearch || m.name.toLowerCase().includes(matrixSearch.toLowerCase()) || m.id.toLowerCase().includes(matrixSearch.toLowerCase());
                  return matchCat && matchSearch;
                });

                return (
                  <div className={styles.tableWrap} style={{ marginTop: '12px' }}>
                    <table className={styles.userTable}>
                      <thead>
                        <tr>
                          <th className={styles.matrixStickyUser}>User</th>
                          <th className={styles.matrixStickyAction} style={{ textAlign: 'center' }}>Action</th>
                          <th style={{ minWidth: '85px' }}>Role</th>
                          <th style={{ minWidth: '120px' }}>Assigned Modules</th>
                          {visibleModules.map(mod => (
                            <th key={mod.id} style={{ textAlign: 'center', minWidth: '105px', fontSize: '0.75rem' }}>
                              <div style={{ fontWeight: 600 }}>{mod.name}</div>
                              <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 400 }}>{mod.category}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(user => {
                          const isAdmin = user.role === 'admin';
                          const assignedModCount = Array.isArray(user.accessibleModules) ? user.accessibleModules.length : 0;

                          return (
                            <tr key={user._id}>
                              <td className={styles.matrixStickyUser}>
                                <div className={styles.userPrimaryInfo}>
                                  <span className={styles.userName}>{user.name}</span>
                                  <span className={styles.userEmail}>{user.email}</span>
                                </div>
                              </td>
                              <td className={styles.matrixStickyAction} style={{ textAlign: 'center' }}>
                                <button
                                  className={styles.btnPrimary}
                                  style={{ padding: '4px 10px', fontSize: '0.74rem', height: '28px' }}
                                  onClick={() => setEditingUser(user)}
                                >
                                  <Edit3 size={12} />
                                  <span>Edit</span>
                                </button>
                              </td>
                              <td>
                                <span className={`${styles.roleBadge} ${isAdmin ? styles.roleAdmin : styles.roleUser}`}>
                                  {user.role}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: isAdmin ? '#c084fc' : '#38bdf8'
                                }}>
                                  {isAdmin ? `All ${ALL_EXTENSION_MODULES.length}` : `${assignedModCount} of ${ALL_EXTENSION_MODULES.length}`}
                                </span>
                              </td>
                              {visibleModules.map(mod => {
                                const hasAccess = isAdmin || (Array.isArray(user.accessibleModules) && user.accessibleModules.includes(mod.id));
                                return (
                                  <td key={mod.id} style={{ textAlign: 'center' }}>
                                    {hasAccess ? (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: 'rgba(34, 197, 94, 0.15)',
                                        color: '#22c55e',
                                        fontSize: '0.8rem',
                                        fontWeight: 700
                                      }}>
                                        ✓
                                      </span>
                                    ) : (
                                      <span style={{ color: '#475569' }}>—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tab 3: Overview & Analytics */}
          {activeTab === 'overview' && (
            <div className={styles.contentCard}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#ffffff' }}>System Overview</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                  Platform capacity, active concurrent connections, and module distribution.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                <div style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  padding: '18px'
                }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '0.9rem', color: '#38bdf8' }}>Role Distribution</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: '#94a3b8' }}>Standard Users</span>
                      <span style={{ fontWeight: 600 }}>{users.filter(u => u.role !== 'admin').length}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: '#94a3b8' }}>System Administrators</span>
                      <span style={{ fontWeight: 600, color: '#c084fc' }}>{users.filter(u => u.role === 'admin').length}</span>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  padding: '18px'
                }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '0.9rem', color: '#4ade80' }}>Active Sessions</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: '#94a3b8' }}>Users Logged In</span>
                      <span style={{ fontWeight: 600, color: '#4ade80' }}>{users.filter(u => u.isUserLoggedin).length}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: '#94a3b8' }}>Total Active Sockets/Tabs</span>
                      <span style={{ fontWeight: 600 }}>{stats.totalActiveLogins || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        isSubmitting={isSubmitting}
      />

      <EditUserModal
        isOpen={Boolean(editingUser)}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSubmit={handleUpdateUser}
        isSubmitting={isSubmitting}
      />

      <ChangePasswordModal
        isOpen={Boolean(passwordUser)}
        user={passwordUser}
        onClose={() => setPasswordUser(null)}
        onSubmit={handleChangePassword}
        isSubmitting={isSubmitting}
      />

      {/* Reset Logins Confirmation Modal */}
      {resetLoginUser && (
        <div className={styles.modalOverlay} onClick={() => setResetLoginUser(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className={styles.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className={`${styles.statIconWrap} ${styles.statIconProjects}`} style={{ width: '36px', height: '36px' }}>
                  <LogOut size={18} />
                </div>
                <h3 className={styles.modalTitle}>Reset Active Logins?</h3>
              </div>
              <button className={styles.modalCloseBtn} onClick={() => setResetLoginUser(null)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                Are you sure you want to terminate all active sessions for <strong>{resetLoginUser.name}</strong> ({resetLoginUser.email})?
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                This will clear all connected tabs/devices and reset their active login counter to 0.
              </p>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setResetLoginUser(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleResetLogins}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Resetting...</span>
                  </>
                ) : (
                  <span>Confirm Reset</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deletingUser && (
        <div className={styles.modalOverlay} onClick={() => setDeletingUser(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className={styles.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className={`${styles.statIconWrap}`} style={{ width: '36px', height: '36px', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                  <ShieldAlert size={18} />
                </div>
                <h3 className={styles.modalTitle}>Delete User Account?</h3>
              </div>
              <button className={styles.modalCloseBtn} onClick={() => setDeletingUser(null)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                Are you sure you want to permanently delete <strong>{deletingUser.name}</strong> ({deletingUser.email})?
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#f87171' }}>
                Warning: This action cannot be undone. All active sessions will be terminated immediately.
              </p>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setDeletingUser(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnDanger}
                style={{ padding: '8px 16px', fontSize: '0.875rem' }}
                onClick={handleDeleteUser}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete User</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
